import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Eye, FileSignature, Loader2, Pencil, Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { ContractDocument } from '../components/ContractDocument';
import { EmptyState } from '../components/EmptyState';
import { HelpHint } from '../components/HelpHint';
import { TableSkeleton } from '../components/Skeleton';
import { PageNav } from '../components/PageNav';
import { useSession } from '../lib/session';

/**
 * The words every contract is drafted from.
 *
 * ## Why this screen had to exist
 *
 * The templates were seeded at boot, stored per organisation, and editable
 * through the API — and by nothing else. Changing a clause meant asking a
 * developer, which for a document that names your prices, your notice period
 * and your obligations is the wrong dependency to have.
 *
 * ## Editing changes the next contract, never a signed one
 *
 * A contract takes a **copy** of the template when it is drafted, and that copy
 * is what gets signed and what is stored. So nothing here can reach backwards
 * into an agreement somebody has already signed — which is the property that
 * makes editing safe to offer at all. It is said on the screen because an admin
 * about to change a price needs to know it.
 *
 * ## Why the placeholders are shown rather than hidden
 *
 * `{{customer.name}}` and the rest are filled in at drafting time. An editor
 * that hid them would be lying about what the document is, and an admin who
 * deletes one by accident should be able to see that they have.
 */
export function ContractTemplatesPage() {
  const queryClient = useQueryClient();
  const { can } = useSession();
  const mayEdit = can('contract:template');

  const [openId, setOpenId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [name, setName] = useState('');
  const [preview, setPreview] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const templates = useQuery({
    queryKey: ['contract-templates'],
    queryFn: api.contractTemplates,
  });

  const chosen = useQuery({
    queryKey: ['contract-template', openId],
    queryFn: () => api.contractTemplate(openId!),
    enabled: Boolean(openId),
  });

  /*
   * The draft follows whichever template was opened, and only then. Keying off
   * the id rather than the object means re-fetching the same template does not
   * throw away edits somebody is part-way through.
   */
  useEffect(() => {
    if (chosen.data && chosen.data.id === openId) {
      setDraft(chosen.data.body);
      setName(chosen.data.name);
      setSaved(false);
      setError(null);
    }
  }, [chosen.data, openId]);

  const save = useMutation({
    mutationFn: () => api.saveContractTemplate(openId!, { name, body: draft }),
    onSuccess: async () => {
      setSaved(true);
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['contract-templates'] });
      await queryClient.invalidateQueries({ queryKey: ['contract-template', openId] });
    },
    onError: (e: Error) => setError(e.message),
  });

  if (templates.isPending) {
    /*
     * Shaped like the page, not like a spinner in a void.
     *
     * The spinner sat in an empty region with the footer floating under it,
     * which reads as a page that has loaded and found nothing. This keeps the
     * heading in place and holds the room the list is about to need.
     */
    return (
      <div className="mx-auto max-w-6xl space-y-6">
        <PageNav crumbs={[{ label: 'Contracts', to: '/contracts' }, { label: 'Wording' }]} />
        <TableSkeleton columns={3} rows={5} label="Loading the wording" />
      </div>
    );
  }

  const rows = templates.data?.templates ?? [];
  const dirty = Boolean(chosen.data) && (draft !== chosen.data?.body || name !== chosen.data?.name);

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <PageNav crumbs={[{ label: 'Contracts', to: '/contracts' }, { label: 'Wording' }]} />

      <div>
        <h1 className="flex items-center gap-1.5 font-heading text-2xl">
          Contract wording
          <HelpHint>
            These are the words a new contract is drafted from. A contract keeps its own copy of
            the wording when it is created, so editing here never changes an agreement anybody
            has already signed.
          </HelpHint>
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
          {mayEdit
            ? 'Editing changes the next contract drafted from a template. Agreements already sent or signed keep the words they were made with.'
            : 'Read-only. An administrator can change these.'}
        </p>
      </div>

      {rows.length === 0 ? (
        <EmptyState
          icon={FileSignature}
          headline="No templates"
          body="They are created when the system starts. If none are here, something stopped that."
        />
      ) : (
        <div className="grid gap-4 lg:grid-cols-[18rem_1fr]">
          <nav className="space-y-2">
            {rows.map((template) => (
              <button
                key={template.id}
                type="button"
                onClick={() => setOpenId(template.id)}
                className={`w-full rounded-xl border p-3 text-left ${
                  openId === template.id
                    ? 'border-primary-500 bg-primary-50/50'
                    : 'border-border hover:bg-muted/40'
                }`}
              >
                <span className="block text-sm font-medium">{template.name}</span>
                <span className="block text-xs text-muted-foreground">
                  {template.tier} · v{template.version}
                  {template.isActive ? '' : ' · switched off'}
                </span>
              </button>
            ))}
          </nav>

          <section className="card">
            {!openId ? (
              <p className="text-sm text-muted-foreground">
                Choose a template on the left to read or edit it.
              </p>
            ) : chosen.isPending ? (
              <Loader2 className="mx-auto my-8 h-5 w-5 animate-spin text-primary-500" />
            ) : (
              <div className="space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <label className="min-w-[14rem] flex-1 text-sm">
                    Name
                    <input
                      className="input mt-1 w-full"
                      value={name}
                      disabled={!mayEdit}
                      onChange={(event) => setName(event.target.value)}
                    />
                  </label>
                  <button
                    type="button"
                    className="btn-quiet mt-5"
                    onClick={() => setPreview((current) => !current)}
                  >
                    {preview ? (
                      <>
                        <Pencil className="h-4 w-4" aria-hidden="true" /> Edit
                      </>
                    ) : (
                      <>
                        <Eye className="h-4 w-4" aria-hidden="true" /> Preview
                      </>
                    )}
                  </button>
                </div>

                {error && (
                  <p className="rounded-xl border border-danger-300 bg-danger-50 p-3 text-sm text-danger-700">
                    {error}
                  </p>
                )}
                {saved && !dirty && (
                  <p className="rounded-xl border border-secondary-300 bg-secondary-50 p-3 text-sm text-secondary-800">
                    Saved. The next contract drafted from this template uses these words.
                  </p>
                )}

                {preview ? (
                  <div className="max-h-[32rem] overflow-y-auto rounded-xl bg-muted/50 p-4">
                    {/*
                      Rendered exactly as the customer will see it, placeholders
                      and all — `{{customer.name}}` is filled in when a contract
                      is drafted, not here, and pretending otherwise would show
                      an admin a document that does not exist.
                    */}
                    <ContractDocument body={draft} className="text-sm" />
                  </div>
                ) : (
                  <label className="block text-sm">
                    The wording
                    <textarea
                      className="input mt-1 h-[32rem] w-full font-mono text-xs leading-relaxed"
                      value={draft}
                      disabled={!mayEdit}
                      spellCheck={false}
                      onChange={(event) => setDraft(event.target.value)}
                    />
                    <span className="mt-1 block text-xs text-muted-foreground">
                      Anything in double braces — {'{{customer.name}}'}, {'{{package.name}}'} — is
                      filled in when a contract is drafted. Leave them exactly as they are unless
                      you mean to remove that detail from every future agreement.
                    </span>
                  </label>
                )}

                {mayEdit && (
                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      className="btn-primary"
                      disabled={!dirty || save.isPending}
                      onClick={() => save.mutate()}
                    >
                      {save.isPending && (
                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                      )}
                      <Save className="h-4 w-4" aria-hidden="true" />
                      Save the wording
                    </button>
                    {dirty && (
                      <button
                        type="button"
                        className="btn-quiet"
                        onClick={() => {
                          setDraft(chosen.data?.body ?? '');
                          setName(chosen.data?.name ?? '');
                        }}
                      >
                        Undo my changes
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
